import React, { useState, useEffect, useRef } from 'react';
import MaterialTable from 'material-table';
import {
  Grid,
  Typography,
  Button,
  Modal,
  TextField,
  FormControl,
  FormLabel,
  Select,
  MenuItem
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import WalletCard from '../components/WalletCard';
import AlertDialog from '../components/AlertDialog';
import AlertDialogTwoButton from '../components/AlertDialogTwoButton';
import PaymentGatewayModal from "../components/PaymentGatewayModal";
import styles from '../styles/landingPage.js';
import { useNavigate } from 'react-router-dom';
import { colors } from '../components/Theme/WebTheme';
import { MAIN_COLOR, SECONDORY_COLOR } from "../common/sharedFunctions";

const useStyles = makeStyles(theme => ({
  ...styles,
  modal: {
    display: 'flex',
    padding: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: 400,
    maxWidth: '92%',
    backgroundColor: "var(--bg-card) !important",
    border: "var(--card-border)",
    boxShadow: "var(--shadow)",
    padding: theme.spacing(2, 4, 3),
    borderRadius: '16px',
    color: 'var(--text-main)'
  },
  inputRtl: {
    "& label": {
      right: 10,
      left: "auto",
      paddingRight: 20
    },
    "& legend": {
      textAlign: "right",
      marginRight: 20
    }
  },
  textField: {
    "& label.Mui-focused": {
      color: '#E9AE31 !important',
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: '#E9AE31 !important',
    },
    "& .MuiFilledInput-underline:after": {
      borderBottomColor: '#E9AE31 !important',
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: '#E9AE31 !important',
      },
      "& fieldset": {
        borderColor: 'var(--border-color) !important',
      },
      "& input": {
        color: 'var(--text-main) !important',
      },
      "& label": {
        color: 'var(--text-secondary) !important',
      }
    },
  },
  selectField: {
    color: "var(--text-main)",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: '#E9AE31 !important',
    },
  },
}));

const icons = {
  'paypal': require('../assets/payment-icons/paypal-logo.png').default,
  'braintree': require('../assets/payment-icons/braintree-logo.png').default,
  'stripe': require('../assets/payment-icons/stripe-logo.png').default,
  'paytm': require('../assets/payment-icons/paytm-logo.png').default,
  'payulatam': require('../assets/payment-icons/payulatam-logo.png').default,
  'flutterwave': require('../assets/payment-icons/flutterwave-logo.png').default,
  'paystack': require('../assets/payment-icons/paystack-logo.png').default,
  'securepay': require('../assets/payment-icons/securepay-logo.png').default,
  'payfast': require('../assets/payment-icons/payfast-logo.png').default,
  'liqpay': require('../assets/payment-icons/liqpay-logo.png').default,
  'culqi': require('../assets/payment-icons/culqi-logo.png').default,
  'mercadopago': require('../assets/payment-icons/mercadopago-logo.png').default,
  'squareup': require('../assets/payment-icons/squareup-logo.png').default,
  'wipay': require('../assets/payment-icons/wipay-logo.png').default,
  'test': require('../assets/payment-icons/test-logo.png').default,
  'razorpay': require('../assets/payment-icons/razorpay-logo.png').default,
  'paymongo': require('../assets/payment-icons/paymongo-logo.png').default,
  'skipcash': require('../assets/payment-icons/skipcash-logo.png').default,
}


const UserWallet = (props) => {
  const {
    withdrawBalance,
  } = api;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  let providers = useSelector(state => state.paymentmethods.providers);
  if (providers && !Array.isArray(providers)) {
    let arr = [];
    let pms = Object.keys(providers);
    for (let i = 0; i < pms.length; i++) {
      if (providers[pms[i]].active) {
        arr.push({
          name: pms[i],
          link: '/' + pms[i] + '-link',
          ...providers[pms[i]]
        });
      }
    }
    providers = arr;
  }
  const [profile, setProfile] = useState();
  const [data, setData] = useState([]);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const [commonAlertTwoButton, setCommonAlertTwoButton] = useState({ open: false, msg: '' });
  const rootRef = useRef(null);
  const [amount, setAmount] = useState(0);
  const [bankName, setBankName] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankAccountHolderName, setBankAccountHolderName] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalInfo, setModalInfo] = useState({
    mOpen: false,
    mType: null
  });
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
  const [paymentModalStatus, setPaymentModalStatus] = useState(false);
  const columns = [
    { title: t('requestDate'), field: 'date', defaultSort: 'desc', render: rowData => rowData.date ? moment(rowData.date).format('lll') : null, },
    {
      title: t('amount'), field: 'amount', editable: 'never',
      render: (rowData) =>
        rowData.amount
          ? settings.swipe_symbol
            ? rowData.amount + " " + settings.symbol
            : settings.symbol + " " + rowData.amount
          : settings.swipe_symbol
            ? "0 " + settings.symbol
            : settings.symbol + " 0",
    },
    { title: t('transaction_id'), field: 'transaction_id', render: rowData => rowData.transaction_id ? rowData.transaction_id : rowData.txRef, },
    {
      title: t('type'), field: 'type', render: (rowData) => {
        const typeColors = {
          debited: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
          credited: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          withdraw: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        };
        const bg = typeColors[rowData.type] || "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)";
        return (
          <div
            style={{
              background: bg,
              color: "white",
              padding: "6px 12px",
              borderRadius: "20px",
              fontWeight: "600",
              width: "120px",
              margin: "auto",
              textAlign: "center",
              fontSize: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}
          >
            {t(rowData.type)}
          </div>
        )
      },
    }
  ];

  useEffect(() => {
    if (providers) {
      setSelectedProvider(providers[0]);
    }
  }, [providers]);

  useEffect(() => {
    if (auth.profile) {
      setProfile(auth.profile);
      let wdata = auth.walletHistory;
      var wallHis = [];
      for (let key in wdata) {
        wdata[key].walletKey = key
        if (wdata[key].type.includes("Credit") || wdata[key].type.includes("credit")) {
          wdata[key].type = 'credited'
        } else if (wdata[key].type.includes("Withdraw") || wdata[key].type.includes("withdraw")) {
          wdata[key].type = 'withdraw'
        } else {
          wdata[key].type = 'debited'
        }
        wallHis.push(wdata[key])
      }
      if (wallHis.length > 0) {
        setData(wallHis.reverse());
      } else {
        setData([]);
      }

    } else {
      setProfile(null);
    }
  }, [auth.profile, auth.walletHistory]);

  const doRecharge = (e) => {
    e.preventDefault();
    if (!(profile.mobile && profile.mobile.length > 6 && profile.email && profile.firstName)) {
      setCommonAlertTwoButton({ open: true, msg: t('profile_incomplete') })
    } else {
      if (providers) {
        setModalInfo({
          mOpen: true,
          mType: 'add'
        });
      } else {
        setCommonAlert({ open: true, msg: t('provider_not_found') })
      }
    }
  }

  const doWithdraw = (e) => {
    e.preventDefault();
    if (!(profile.mobile && profile.mobile.length > 6 && profile.email && profile.firstName)) {
      setCommonAlertTwoButton({ open: true, msg: t('profile_incomplete') })
    } else {
      if (parseFloat(profile.walletBalance) > 0) {
        setBankName(profile.bankName || '');
        setBankCode(profile.bankCode || '');
        setBankAccount(profile.bankAccount || '');
        setBankAccountHolderName(profile.bankAccountHolderName || '');
        setModalInfo({
          mOpen: true,
          mType: 'withdraw'
        });
      } else {
        setCommonAlert({ open: true, msg: t('wallet_bal_low') })
      }
    }
  }

  const gotoOperation = () => {
    navigate('/profile');
    setCommonAlert({ open: false, msg: '' })
  };

  const cancelOperation = () => {
    setAmount(0);
    setModalInfo({
      mOpen: false,
      mType: null
    });
    setCommonAlert({ open: false, msg: '' })
  };

  const cancelOperationTowButton = () => {
    setAmount(0);
    setModalInfo({
      mOpen: false,
      mType: null
    });
    setCommonAlertTwoButton({ open: false, msg: '' })
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (amount <= 0) {
      setCommonAlert({ open: true, msg: t('valid_amount') });
    } else {
      if (modalInfo.mType === 'add') {
        setModalInfo({
          ...modalInfo,
          mOpen: false
        });
        setPaymentModalStatus(true);
      } else {
        if (parseFloat(profile.walletBalance) < amount) {
          setCommonAlert({ open: true, msg: t('withdraw_more') });
        } else {
          if (!bankAccountHolderName || !bankAccount || !bankName || !bankCode) {
            setCommonAlert({ open: true, msg: t('fill_bank_details') || "Please fill all Bank details" });
            return;
          }

          const updatedProfile = {
            ...profile,
            bankName,
            bankCode,
            bankAccount,
            bankAccountHolderName,
          };

          dispatch(withdrawBalance(updatedProfile, amount));
          cancelOperation();
        }
      }
    }
  }

  const createOrderId = () => {
    const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const reference = [...Array(4)].map(_ => c[~~(Math.random() * c.length)]).join('');
    return "wallet-" + auth.profile.uid + "-" + reference
  }

  const handlePaymentModalClose = (e) => {
    setTimeout(() => {
      setPaymentModalStatus(false);
    }, 1500);
  }


  return (
    <div className="responsive-container" style={{ minHeight: '100vh', backgroundColor: 'var(--bg-main)', direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <div style={{ borderRadius: "19px", padding: "10px 0" }}>
        <Typography variant="h4" style={{ margin: "10px 10px 24px 0", textAlign: isRTL === 'rtl' ? 'right' : 'left', color: 'var(--text-main)', fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>{t('my_wallet_title')}</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} style={{ textAlign: 'center' }}>
            {settings.swipe_symbol === false ?
              <WalletCard crdStyle={{ backgroundColor: 'var(--bg-card)', borderRadius: '16px', border: '1px solid rgba(233, 174, 49, 0.3)', boxShadow: 'var(--shadow-md)', padding: '24px' }} >
                <Typography variant="h6" style={{ color: '#E9AE31', fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Balance')}</Typography>
                <Typography variant="h4" style={{ color: 'var(--text-main)', fontFamily: 'Outfit, sans-serif', fontWeight: 700, marginTop: '8px' }}>{settings.symbol + ' ' + (profile && profile.hasOwnProperty('walletBalance') ? parseFloat(profile.walletBalance).toFixed(settings.decimal || 2) : '0.00')}</Typography></WalletCard>
              :
              <WalletCard crdStyle={{ backgroundColor: 'var(--bg-card)', borderRadius: '16px', border: '1px solid rgba(233, 174, 49, 0.3)', boxShadow: 'var(--shadow-md)', padding: '24px' }}>
                <Typography variant="h6" style={{ color: '#E9AE31', fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Balance')}</Typography>
                <Typography variant="h4" style={{ color: 'var(--text-main)', fontFamily: 'Outfit, sans-serif', fontWeight: 700, marginTop: '8px' }}>{(profile && profile.hasOwnProperty('walletBalance') ? parseFloat(profile.walletBalance).toFixed(settings.decimal || 2) : '0.00') + ' ' + settings.symbol}</Typography></WalletCard>
            }
          </Grid>
          <Grid item xs={12} sm={4} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              onClick={doRecharge}
              variant="contained"
              style={{
                width: '100%',
                maxWidth: '240px',
                background: 'linear-gradient(135deg, #E9AE31 0%, #c58d1b 100%)',
                color: '#08080A',
                borderRadius: '12px',
                fontWeight: 700,
                textTransform: 'none',
                padding: '14px 20px',
                boxShadow: '0 4px 12px rgba(233, 174, 49, 0.25)',
                border: 'none'
              }}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(233, 174, 49, 0.4)'
                }
              }}
              size='large'
            >
              {t('add_to_wallet')}
            </Button>
          </Grid>
          {profile && (profile.usertype === 'driver' || (profile.usertype === 'customer' && settings && settings.RiderWithDraw)) ?
            <Grid item xs={12} sm={4} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Button
                onClick={doWithdraw}
                variant="contained"
                style={{
                  width: '100%',
                  maxWidth: '240px',
                  background: 'var(--bg-card)',
                  color: '#E9AE31',
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  padding: '14px 20px',
                  border: '1px solid #E9AE31',
                  boxShadow: 'var(--shadow-sm)'
                }}
                sx={{
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: '#E9AE31',
                    boxShadow: 'var(--shadow-md)'
                  }
                }}
                size='large'
              >
                {t('withdraw')}
              </Button>
            </Grid>
            : null}
        </Grid>
      </div>

      <div style={{ overflowX: 'auto', width: '100%', borderRadius: '20px', marginTop: 30, boxShadow: "var(--shadow)", border: "var(--card-border)" }}>
        <MaterialTable
          title={t('transaction_history_title')}
          columns={columns}
          style={{
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
            padding: "0",
            backgroundColor: "var(--bg-card)",
            overflow: "hidden",
            border: "none",
            boxShadow: "none"
          }}
          data={data}
          onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
          options={{
            exportButton: true,
            rowStyle: rowData => ({
              backgroundColor: (selectedRow === rowData.tableData.id) ? "var(--table-row-selected)" : "var(--table-row-bg)",
              borderBottom: "var(--table-border)",
              fontSize: "14px",
              color: "var(--text-main)"
            }),
            editable: {
              backgroundColor: "var(--bg-main)",
              fontSize: "0.8em",
              fontWeight: 'bold ',
              fontFamily: '"Outfit", "Helvetica", "Arial", sans-serif',
            },
            headerStyle: {
              position: "sticky",
              top: "0px",
              background: "var(--table-header-bg)",
              color: "var(--text-light)",
              textAlign: "center",
              fontWeight: "700",
              fontFamily: 'Inter, sans-serif',
              border: "none",
              height: "56px",
              borderBottom: "var(--card-border)"
            },
            cellStyle: {
              borderBottom: "var(--table-border)",
              textAlign: "center",
              padding: "14px",
              color: "var(--text-main)"
            },
            actionsColumnIndex: -1,
          }}
          localization={{
            toolbar: {
              searchPlaceholder: (t('search')),
              exportTitle: (t('export')),
            },
            pagination: {
              labelDisplayedRows: ('{from}-{to} ' + (t('of')) + ' {count}'),
              firstTooltip: (t('first_page_tooltip')),
              previousTooltip: (t('previous_page_tooltip')),
              nextTooltip: (t('next_page_tooltip')),
              lastTooltip: (t('last_page_tooltip'))
            },
          }}
        />
      </div>

      <PaymentGatewayModal
        open={paymentModalStatus}
        onClose={handlePaymentModalClose}
        orderId={createOrderId()}
        amount={amount}
        currency={settings?.code}
        productName={t('add_money')}
        booking={null}
      />
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={modalInfo.mOpen}
        onClose={cancelOperation}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Grid container spacing={1} className={classes.paper} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', maxHeight: '85vh', overflowY: 'auto' }}>
          <Typography component="h2" variant="h5" style={{ marginTop: 15, color: 'var(--text-main)', fontFamily: 'Outfit, sans-serif', fontWeight: 600, width: '100%' }}>
            {modalInfo.mType === 'add' ? t('add_to_wallet') : t('withdraw_money')}
          </Typography>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="amount"
              label={t('amount')}
              name="amount"
              autoComplete="amount"
              onChange={(e) => {
                try {
                  if (e.target.value === "") {
                    setAmount(0);
                  } else {
                    setAmount(parseFloat(e.target.value));
                  }
                } catch (e) {
                  setCommonAlert({ open: true, msg: t('no_details_error') });
                  setAmount(0);
                }
              }}
              value={amount.toString()}
              autoFocus
              className={`${isRTL === 'rtl' ? classes.inputRtl : ''} ${classes.textField}`}
              style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
            />
          </Grid>

          {modalInfo.mType === 'withdraw' ? (
            <>
              <Typography component="h3" variant="h6" style={{ marginTop: 15, color: 'var(--text-main)', fontFamily: 'Outfit, sans-serif', fontWeight: 600, width: '100%', borderBottom: '1px solid var(--border-color)', paddingBottom: 5 }}>
                {t('bank_details') || 'Bank Details'}
              </Typography>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="bankAccountHolderName"
                  label={t('bankAccountHolderName') || 'Account Holder Full Name'}
                  name="bankAccountHolderName"
                  onChange={(e) => setBankAccountHolderName(e.target.value)}
                  value={bankAccountHolderName}
                  className={`${isRTL === 'rtl' ? classes.inputRtl : ''} ${classes.textField}`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="bankAccount"
                  label={t('bankAccount') || 'Account Number'}
                  name="bankAccount"
                  onChange={(e) => setBankAccount(e.target.value)}
                  value={bankAccount}
                  className={`${isRTL === 'rtl' ? classes.inputRtl : ''} ${classes.textField}`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="bankName"
                  label={t('bankName')}
                  name="bankName"
                  onChange={(e) => setBankName(e.target.value)}
                  value={bankName}
                  className={`${isRTL === 'rtl' ? classes.inputRtl : ''} ${classes.textField}`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="bankCode"
                  label={t('bankCode') || 'IFSC Code'}
                  name="bankCode"
                  onChange={(e) => setBankCode(e.target.value)}
                  value={bankCode}
                  className={`${isRTL === 'rtl' ? classes.inputRtl : ''} ${classes.textField}`}
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12} sm={12} md={12} lg={12} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', marginTop: 20 }}>
            <Button onClick={cancelOperation} variant="contained" color="primary" style={{ padding: '8px 24px', backgroundColor: '#ef4444', borderRadius: '12px', fontWeight: 600, textTransform: 'none' }}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary" style={{ marginLeft: 10, padding: '8px 24px', backgroundColor: '#10b981', borderRadius: '12px', fontWeight: 600, textTransform: 'none' }}>
              {modalInfo.mType === 'add' ? t('add_to_wallet') : t('withdraw')}
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <AlertDialog open={commonAlert.open} onClose={cancelOperation}>{commonAlert.msg}</AlertDialog>
      <AlertDialogTwoButton open={commonAlertTwoButton.open} onClose={cancelOperationTowButton} onGoto={gotoOperation}>{commonAlertTwoButton.msg}</AlertDialogTwoButton>
    </div>
  );
}

export default UserWallet;
